/**
 * Copiloto de call — contrato unico entre a extensao do Chrome e o gateway
 * WebSocket do servidor (server/copiloto). Mudou aqui, quebra nos dois lados
 * no build. E esse o ponto.
 *
 * O audio vai em frames binarios: PCM 16 bits, 16 kHz, 2 canais intercalados.
 * Canal 0 e o microfone do closer, canal 1 e a aba da call. Por isso nao existe
 * diarizacao em lugar nenhum: quem falou vem do canal, nunca de um palpite.
 */

export const COPILOTO_AUDIO = {
  sampleRate: 16000,
  canais: 2,
  encoding: "linear16" as const,
  chunkMs: 200,
};

/** Caminho do WebSocket no servidor do Lead Conversa. */
export const COPILOTO_WS_PATH = "/api/copiloto/ws";

export type CopilotoCanal = "closer" | "lead";

export type CopilotoEstagio =
  | "sondagem"
  | "apresentacao"
  | "proposta"
  | "objecao"
  | "fechamento";

export const COPILOTO_ESTAGIOS: CopilotoEstagio[] = [
  "sondagem",
  "apresentacao",
  "proposta",
  "objecao",
  "fechamento",
];

export type CopilotoEventoTipo =
  | "objecao"
  | "sinal_compra"
  | "pergunta_tecnica"
  | "risco"
  | "nenhum";

export type CopilotoSubtipo =
  | "preco"
  | "timing"
  | "autoridade"
  | "concorrente"
  | "ceticismo"
  | "escopo"
  | "nenhum";

export const COPILOTO_SUBTIPOS: Exclude<CopilotoSubtipo, "nenhum">[] = [
  "preco",
  "timing",
  "autoridade",
  "concorrente",
  "ceticismo",
  "escopo",
];

export type CopilotoChunkTipo = "script" | "objecao" | "prova" | "preco" | "caso" | "politica";

export const COPILOTO_CHUNK_TIPOS: CopilotoChunkTipo[] = [
  "script",
  "objecao",
  "prova",
  "preco",
  "caso",
  "politica",
];

export type CopilotoPlataforma = "meet" | "zoom" | "outro";

/**
 * O que a extensao sabe da call. Produto e ticket vem das configuracoes do
 * workspace; a extensao so informa a plataforma e, quando souber, o contato
 * ou a oportunidade para a sessao ficar ligada ao CRM.
 */
export interface CopilotoContextoCliente {
  plataforma: CopilotoPlataforma;
  contactId?: number;
  dealId?: number;
  /** Produto do CRM escolhido pelo vendedor ao ligar. Filtra o playbook (empresa + produto). */
  productId?: number;
}

/** Resposta de GET /api/copiloto/contexto (Bearer token da extensao): o que o popup mostra antes de ligar. */
export interface CopilotoContextoExtensao {
  usuario: string;
  workspace: string;
  produtoPadrao: string;
  produtos: { id: number; nome: string; preco: string | null }[];
}

/** Extensao para gateway. Audio vai em frames binarios, o resto em JSON. */
export type CopilotoMensagemCliente =
  | { type: "hello"; token: string; contexto: CopilotoContextoCliente }
  | { type: "socorro"; nota?: string }
  | { type: "feedback"; sugestaoId: string; acao: CopilotoFeedbackAcao }
  | { type: "bye" };

export type CopilotoFeedbackAcao = "copiou" | "usei" | "descartou";

/** Gateway para extensao. */
export type CopilotoMensagemServidor =
  | { type: "pronto"; sessaoId: number; usuario: string; produto: string }
  | { type: "transcricao"; canal: CopilotoCanal; texto: string; final: boolean; tMs: number }
  | {
      type: "evento";
      id: string;
      tipo: CopilotoEventoTipo;
      subtipo: CopilotoSubtipo;
      trecho: string;
      confianca: number;
      tMs: number;
    }
  | {
      type: "sugestao";
      id: string;
      eventoId: string | null;
      principal: string;
      alternativa: string;
      ancora: string;
      proximaPergunta: string;
      /** Do inicio da geracao ate o envio. */
      latenciaMs: number;
      /** Do fim da fala do lead ate o envio — o numero que importa para o closer. */
      latenciaDesdeFalaMs: number | null;
    }
  | { type: "erro"; mensagem: string; fatal?: boolean };

/** Resposta do gerador quando o playbook nao cobre a situacao. */
export const COPILOTO_SEM_COBERTURA = "SEM COBERTURA";

/**
 * Politica de disparo, editavel por workspace. Interromper a toa e pior que
 * ficar calado: os padroes sao conservadores de proposito.
 */
export interface CopilotoPolitica {
  /** Quantos segundos de conversa o detector enxerga. */
  janelaSeg: number;
  /** Silencio minimo entre duas sugestoes, em segundos. */
  intervaloMinimoSeg: number;
  /** Uma objecao de outro subtipo, ainda nao tratada, pode furar o intervalo depois destes segundos. */
  intervaloOutroSubtipoSeg: number;
  /** Abaixo disto o detector fica calado. */
  confiancaMinima: number;
  /** Teto de sugestoes por hora de call. */
  tetoPorHora: number;
  /** Sem gatilho lexical, o detector so roda de novo depois destes segundos. */
  segundosSemGatilhoAteAvaliar: number;
}

export const COPILOTO_POLITICA_PADRAO: CopilotoPolitica = {
  janelaSeg: 45,
  intervaloMinimoSeg: 40,
  intervaloOutroSubtipoSeg: 15,
  confiancaMinima: 0.6,
  tetoPorHora: 12,
  segundosSemGatilhoAteAvaliar: 25,
};

/** Termos que acendem o detector sem gastar LLM. Um conjunto por subtipo. */
export type CopilotoGatilhos = Record<Exclude<CopilotoSubtipo, "nenhum">, string[]> & {
  sinalCompra: string[];
};

export const COPILOTO_GATILHOS_PADRAO: CopilotoGatilhos = {
  preco: ["caro", "valor alto", "valor bem alto", "orcamento", "verba", "desconto", "nao cabe", "investimento alto"],
  timing: ["agora nao", "nao e o momento", "ano que vem", "proximo semestre", "depois do", "vou pensar", "me da um tempo", "mais pra frente"],
  autoridade: ["preciso falar com", "levar para a diretoria", "nao decido sozinh", "depende do", "passar para o juridico", "alinhar internamente", "quem decide"],
  concorrente: ["ja usamos", "ja temos contrato", "recebi proposta", "estamos com outro", "concorrente", "outra empresa"],
  ceticismo: ["ja tentamos", "nao funcionou", "sera que funciona", "como voces garantem", "nao acredito", "na pratica", "duvido"],
  escopo: ["nao e bem isso", "precisava de algo", "atende tambem", "consegue fazer", "no nosso caso e diferente", "nao serve"],
  sinalCompra: [
    "como funciona a implantacao",
    "quanto tempo leva",
    "quem seria o responsavel",
    "da para comecar",
    "como faco para contratar",
    "manda a proposta",
    "quais sao os proximos passos",
    "como e o pagamento",
  ],
};

/** Mensagens trocadas entre a pagina do Lead Conversa e a extensao, via window.postMessage. */
export const COPILOTO_PONTE = {
  /** Pagina pergunta se a extensao esta instalada. */
  ping: "lc-copiloto:ping",
  /** Extensao responde com versao e se ja tem conta conectada. */
  pong: "lc-copiloto:pong",
  /** Pagina entrega o token da conta para a extensao guardar. */
  conectar: "lc-copiloto:conectar",
  /** Extensao confirma que guardou. */
  conectado: "lc-copiloto:conectado",
  /** Pagina pede para a extensao esquecer a conta. */
  desconectar: "lc-copiloto:desconectar",
} as const;
